// src/modules/Admin/pages/Products/ProductEditor.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaSave, FaArrowLeft, FaUpload, FaTimes, FaBoxOpen } from 'react-icons/fa';
import { supabase } from '../../../../config/supabase';

const PageContainer = styled.div`
  max-width: 900px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  h1 {
    font-size: 24px;
    font-weight: 600;
    color: #111;
    display: flex;
    align-items: center;
    gap: 12px;

    svg {
      color: #00D4AA;
    }
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #666;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    color: #111;
  }
`;

const Form = styled.form`
  background: white;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }

  h2 {
    font-size: 16px;
    font-weight: 600;
    color: #111;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #eee;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$cols || 2}, 1fr);
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.$mb || '0'};

  label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;
  }

  input, select, textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: #00D4AA;
    }

    &::placeholder {
      color: #999;
    }
  }

  textarea {
    min-height: 120px;
    resize: vertical;
  }

  small {
    display: block;
    margin-top: 4px;
    font-size: 12px;
    color: #999;
  }
`;

const ImageUpload = styled.div`
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    border-color: #00D4AA;
    background: rgba(0, 212, 170, 0.02);
  }

  input {
    display: none;
  }

  svg {
    font-size: 32px;
    color: #999;
    margin-bottom: 8px;
  }

  p {
    color: #666;
    font-size: 14px;
  }
`;

const ImagePreview = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
`;

const PreviewItem = styled.div`
  position: relative;
  width: 100px;
  height: 100px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }

  button {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 24px;
    height: 24px;
    background: #ef4444;
    color: white;
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 10px;
  }
`;

const CheckboxGroup = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #333;

  input {
    width: 18px;
    height: 18px;
    accent-color: #00D4AA;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #eee;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 200ms;

  ${props => props.$primary ? `
    background: #00D4AA;
    color: #000;
    border: none;

    &:hover {
      opacity: 0.9;
    }
  ` : `
    background: white;
    color: #666;
    border: 1px solid #ddd;

    &:hover {
      border-color: #111;
      color: #111;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const generateSlug = (name) => {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

const ProductEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    short_description: '',
    description: '',
    price: '',
    compare_price: '',
    cost: '',
    sku: '',
    stock: '0',
    track_stock: true,
    weight_kg: '',
    height_cm: '',
    width_cm: '',
    length_cm: '',
    category_id: '',
    tags: '',
    meta_title: '',
    meta_description: '',
    is_active: true,
    is_featured: false,
    images: []
  });

  useEffect(() => {
    fetchCategories();
    if (isEditing) {
      fetchProduct();
    }
  }, [id]);

  const fetchCategories = async () => {
    const { data } = await supabase
      .from('shop_categories')
      .select('*')
      .eq('is_active', true)
      .order('name');

    setCategories(data || []);
  };

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('shop_products')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data) {
        setFormData({
          ...data,
          price: data.price?.toString() || '',
          compare_price: data.compare_price?.toString() || '',
          cost: data.cost?.toString() || '',
          stock: data.stock?.toString() || '0',
          weight_kg: data.weight_kg?.toString() || '',
          height_cm: data.height_cm?.toString() || '',
          width_cm: data.width_cm?.toString() || '',
          length_cm: data.length_cm?.toString() || '',
          tags: data.tags?.join(', ') || '',
          images: data.images || []
        });
      }
    } catch (error) {
      console.error('Erro ao buscar produto:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue,
      ...(name === 'name' && !isEditing ? { slug: generateSlug(value) } : {})
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);

    for (const file of files) {
      try {
        const fileName = `products/${Date.now()}-${file.name}`;
        const { error } = await supabase.storage
          .from('shop')
          .upload(fileName, file);

        if (error) throw error;

        const { data: urlData } = supabase.storage
          .from('shop')
          .getPublicUrl(fileName);

        setFormData(prev => ({
          ...prev,
          images: [...prev.images, urlData.publicUrl]
        }));
      } catch (error) {
        console.error('Erro ao fazer upload:', error);
        alert('Erro ao fazer upload da imagem');
      }
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const productData = {
        name: formData.name,
        slug: formData.slug,
        short_description: formData.short_description || null,
        description: formData.description || null,
        price: parseFloat(formData.price) || 0,
        compare_price: formData.compare_price ? parseFloat(formData.compare_price) : null,
        cost: formData.cost ? parseFloat(formData.cost) : null,
        sku: formData.sku || null,
        stock: parseInt(formData.stock) || 0,
        track_stock: formData.track_stock,
        weight_kg: formData.weight_kg ? parseFloat(formData.weight_kg) : null,
        height_cm: formData.height_cm ? parseFloat(formData.height_cm) : null,
        width_cm: formData.width_cm ? parseFloat(formData.width_cm) : null,
        length_cm: formData.length_cm ? parseFloat(formData.length_cm) : null,
        category_id: formData.category_id || null,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        meta_title: formData.meta_title || null,
        meta_description: formData.meta_description || null,
        is_active: formData.is_active,
        is_featured: formData.is_featured,
        images: formData.images,
        updated_at: new Date().toISOString()
      };

      let error;

      if (isEditing) {
        const result = await supabase
          .from('shop_products')
          .update(productData)
          .eq('id', id);
        error = result.error;
      } else {
        const result = await supabase
          .from('shop_products')
          .insert(productData);
        error = result.error;
      }

      if (error) throw error;

      navigate('/admin/produtos');
    } catch (error) {
      console.error('Erro ao salvar produto:', error);
      alert('Erro ao salvar produto: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageContainer>Carregando...</PageContainer>;
  }

  return (
    <PageContainer>
      <PageHeader>
        <div>
          <BackButton onClick={() => navigate('/admin/produtos')}>
            <FaArrowLeft /> Voltar
          </BackButton>
          <h1>
            <FaBoxOpen /> {isEditing ? 'Editar Produto' : 'Novo Produto'}
          </h1>
        </div>
      </PageHeader>

      <Form onSubmit={handleSubmit}>
        <Section>
          <h2>Informacoes Basicas</h2>
          <FormGrid>
            <FormGroup>
              <label>Nome do Produto *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Ex: Braco Robotico VR-100"
                required
              />
            </FormGroup>
            <FormGroup>
              <label>Slug (URL)</label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="braco-robotico-vr-100"
              />
              <small>Gerado automaticamente a partir do nome</small>
            </FormGroup>
          </FormGrid>

          <FormGroup $mb="16px">
            <label>Descricao Curta</label>
            <input
              type="text"
              name="short_description"
              value={formData.short_description}
              onChange={handleChange}
              placeholder="Breve descricao para listagens"
            />
          </FormGroup>

          <FormGroup>
            <label>Descricao Completa</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Descricao detalhada do produto"
            />
          </FormGroup>
        </Section>

        <Section>
          <h2>Imagens</h2>
          <ImageUpload>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              id="image-upload"
            />
            <label htmlFor="image-upload" style={{ cursor: 'pointer' }}>
              <FaUpload />
              <p>Clique para adicionar imagens</p>
            </label>
          </ImageUpload>

          {formData.images.length > 0 && (
            <ImagePreview>
              {formData.images.map((img, index) => (
                <PreviewItem key={index}>
                  <img src={img} alt={`Preview ${index + 1}`} />
                  <button type="button" onClick={() => handleRemoveImage(index)}>
                    <FaTimes />
                  </button>
                </PreviewItem>
              ))}
            </ImagePreview>
          )}
        </Section>

        <Section>
          <h2>Precos</h2>
          <FormGrid $cols={3}>
            <FormGroup>
              <label>Preco de Venda (R$) *</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                required
              />
            </FormGroup>
            <FormGroup>
              <label>Preco Comparativo (R$)</label>
              <input
                type="number"
                name="compare_price"
                value={formData.compare_price}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
              <small>Preco "De" (riscado)</small>
            </FormGroup>
            <FormGroup>
              <label>Custo (R$)</label>
              <input
                type="number"
                name="cost"
                value={formData.cost}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
              <small>Apenas para controle interno</small>
            </FormGroup>
          </FormGrid>
        </Section>

        <Section>
          <h2>Estoque</h2>
          <FormGrid $cols={3}>
            <FormGroup>
              <label>SKU</label>
              <input
                type="text"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                placeholder="VR-100-001"
              />
            </FormGroup>
            <FormGroup>
              <label>Quantidade em Estoque</label>
              <input
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                min="0"
              />
            </FormGroup>
            <FormGroup style={{ display: 'flex', alignItems: 'flex-end' }}>
              <CheckboxGroup>
                <input
                  type="checkbox"
                  name="track_stock"
                  checked={formData.track_stock}
                  onChange={handleChange}
                />
                Controlar estoque
              </CheckboxGroup>
            </FormGroup>
          </FormGrid>
        </Section>

        <Section>
          <h2>Dimensoes (para frete)</h2>
          <FormGrid $cols={4}>
            <FormGroup>
              <label>Peso (kg)</label>
              <input
                type="number"
                name="weight_kg"
                value={formData.weight_kg}
                onChange={handleChange}
                placeholder="0.000"
                step="0.001"
                min="0"
              />
            </FormGroup>
            <FormGroup>
              <label>Altura (cm)</label>
              <input
                type="number"
                name="height_cm"
                value={formData.height_cm}
                onChange={handleChange}
                placeholder="0"
                step="0.1"
                min="0"
              />
            </FormGroup>
            <FormGroup>
              <label>Largura (cm)</label>
              <input
                type="number"
                name="width_cm"
                value={formData.width_cm}
                onChange={handleChange}
                placeholder="0"
                step="0.1"
                min="0"
              />
            </FormGroup>
            <FormGroup>
              <label>Comprimento (cm)</label>
              <input
                type="number"
                name="length_cm"
                value={formData.length_cm}
                onChange={handleChange}
                placeholder="0"
                step="0.1"
                min="0"
              />
            </FormGroup>
          </FormGrid>
        </Section>

        <Section>
          <h2>Organizacao</h2>
          <FormGrid>
            <FormGroup>
              <label>Categoria</label>
              <select
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
              >
                <option value="">Selecione uma categoria</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </FormGroup>
            <FormGroup>
              <label>Tags</label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="robotica, automacao, industrial"
              />
              <small>Separadas por virgula</small>
            </FormGroup>
          </FormGrid>

          <FormGrid style={{ marginTop: '16px' }}>
            <CheckboxGroup>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />
              Produto ativo (visivel na loja)
            </CheckboxGroup>
            <CheckboxGroup>
              <input
                type="checkbox"
                name="is_featured"
                checked={formData.is_featured}
                onChange={handleChange}
              />
              Produto em destaque
            </CheckboxGroup>
          </FormGrid>
        </Section>

        <Section>
          <h2>SEO</h2>
          <FormGroup $mb="16px">
            <label>Titulo para SEO</label>
            <input
              type="text"
              name="meta_title"
              value={formData.meta_title}
              onChange={handleChange}
              placeholder="Titulo personalizado para buscadores"
            />
          </FormGroup>
          <FormGroup>
            <label>Descricao para SEO</label>
            <textarea
              name="meta_description"
              value={formData.meta_description}
              onChange={handleChange}
              placeholder="Descricao para buscadores (max 160 caracteres)"
              style={{ minHeight: '80px' }}
            />
          </FormGroup>
        </Section>

        <Actions>
          <Button type="button" onClick={() => navigate('/admin/produtos')}>
            Cancelar
          </Button>
          <Button type="submit" $primary disabled={saving}>
            <FaSave /> {saving ? 'Salvando...' : 'Salvar Produto'}
          </Button>
        </Actions>
      </Form>
    </PageContainer>
  );
};

export default ProductEditor;
